import React, { useState, useEffect } from 'react';
import { Layout, Menu, Grid, Drawer } from 'antd';
import './Sidebar.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeOutlined,
  AppstoreOutlined,
  ApiOutlined,
  DotChartOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  UserOutlined,
  LockOutlined,
  SettingOutlined,
  RobotOutlined,
  KeyOutlined,
  CommentOutlined,
  EditOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { useUserSession } from '../contexts/UserSessionContext';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

const CHANNEL_CHILDREN = [
  { key: '/channels/widgets', label: 'Widgets', to: '/channels/widgets', icon: <CommentOutlined /> },
  { key: '/channels/bots', label: 'Bots', to: '/channels/bots', icon: <RobotOutlined /> },
  { key: '/channels/api', label: 'API Keys', to: '/channels/api', icon: <KeyOutlined /> },
];

const MAIN_NAV_BEFORE_CHANNELS = [
  { key: '/dashboard/overview', icon: <HomeOutlined />, label: 'Dashboard', to: '/dashboard/overview' },
  { key: '/collections', icon: <DatabaseOutlined />, label: 'Collections', to: '/collections' },
  { key: '/assistants', icon: <AppstoreOutlined />, label: 'Assistants', to: '/assistants' },
  { key: '/prompts', icon: <EditOutlined />, label: 'Prompts', to: '/prompts' },
];

const MAIN_NAV_AFTER_CHANNELS = [
  { key: '/connectors', icon: <ToolOutlined />, label: 'Tools', to: '/connectors' },
  { key: '/vault', icon: <SafetyOutlined />, label: 'Vault', to: '/vault' },
  { key: '/benchmarking', icon: <DotChartOutlined />, label: 'Benchmarking', to: '/benchmarking' },
];

const Sidebar = ({ collapsed, mobileDrawerVisible, onMobileDrawerClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { isAdmin } = useUserSession();
  const [openKeys, setOpenKeys] = useState([]);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    if (location.pathname.startsWith('/channels')) {
      setOpenKeys((prev) => (prev.includes('channels') ? prev : [...prev, 'channels']));
    } else {
      setOpenKeys((prev) => prev.filter((k) => k !== 'channels'));
    }
  }, [location.pathname]);

  const mainNavItems = [...MAIN_NAV_BEFORE_CHANNELS, ...MAIN_NAV_AFTER_CHANNELS];

  const adminNavItems = [
    { key: '/users', icon: <UserOutlined />, label: 'Users', to: '/users' },
    { key: '/platform-settings', icon: <SettingOutlined />, label: 'Settings', to: '/platform-settings' },
    { key: process.env.REACT_APP_ENV === 'PRODUCTION' ? `https://${process.env.REACT_APP_KEYCLOAK_DOMAIN}/auth` : 'http://localhost:8080/', icon: <LockOutlined />, label: 'Keycloak', external: true },
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return '/dashboard/overview';
    if (path.startsWith('/s/')) return '';
    if (path.startsWith('/chat/')) return '/assistants';
    if (path.match(/^\/assistants\/[^/]+\/channels/)) return '/assistants';
    if (path === '/channels' || path === '/channels/') return '/channels/widgets';
    if (path.startsWith('/channels/widgets')) return '/channels/widgets';
    if (path.startsWith('/channels/bots')) return '/channels/bots';
    if (path.startsWith('/channels/api')) return '/channels/api';
    const allItems = [...mainNavItems, ...adminNavItems];
    const match = allItems.find((item) => path.startsWith(item.key));
    return match ? match.key : '/dashboard/overview';
  };

  const selectedKey = getSelectedKey();

  const promptLogin = () => {
    keycloak.login({ redirectUri: window.location.href });
  };

  const handleMenuClick = (item) => {
    if (!keycloak.authenticated) {
      promptLogin();
      return;
    }
    if (item.external) {
      window.open(item.key, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.to);
      if (isMobile && onMobileDrawerClose) {
        onMobileDrawerClose();
      }
    }
  };

  const renderMainMenuItems = (items, showLinks) =>
    items.map((item) => (
      <Menu.Item
        key={item.key}
        icon={item.icon}
        title={collapsed ? item.label : undefined}
        onClick={() => handleMenuClick(item)}
      >
        {showLinks ? <Link to={item.to}>{item.label}</Link> : item.label}
      </Menu.Item>
    ));

  const renderChannelsSubMenu = (showLinks) => (
    <Menu.SubMenu
      key="channels"
      icon={<ApiOutlined />}
      title="Channels"
    >
      {CHANNEL_CHILDREN.map((child) => (
        <Menu.Item
          key={child.key}
          icon={child.icon}
          onClick={() => {
            navigate(child.to);
            if (isMobile && onMobileDrawerClose) onMobileDrawerClose();
          }}
        >
          {showLinks ? <Link to={child.to}>{child.label}</Link> : child.label}
        </Menu.Item>
      ))}
    </Menu.SubMenu>
  );

  const renderAdminItems = (showLinks) =>
    adminNavItems.map((item) => (
      <Menu.Item
        key={item.key}
        icon={item.icon}
        title={collapsed ? item.label : undefined}
        onClick={() => handleMenuClick(item)}
      >
        {showLinks &&
          (item.external ? (
            <a href={item.key} target="_blank" rel="noopener noreferrer">
              {item.label}
            </a>
          ) : (
            <Link to={item.to}>{item.label}</Link>
          ))}
        {!showLinks && item.label}
      </Menu.Item>
    ));

  if (!keycloak.authenticated) {
    const guest = (
      <div className="sidebar-guest">
        <div className="sidebar-guest-title">Sign in</div>
        <p className="sidebar-guest-copy">Your chats and assistants stay with your account.</p>
        <button type="button" className="sidebar-guest-action" onClick={promptLogin}>
          Sign in
        </button>
      </div>
    );
    if (isMobile) {
      return (
        <Drawer
          title={null}
          placement="left"
          onClose={onMobileDrawerClose}
          open={mobileDrawerVisible}
          className="mobile-sidebar-drawer"
          width={280}
        >
          {guest}
        </Drawer>
      );
    }
    return (
      <Sider
        width={240}
        collapsedWidth={72}
        className="app-sidebar"
        collapsed={collapsed}
        trigger={null}
        style={{
          position: 'fixed',
          left: 0,
          top: 'var(--header-height)',
          bottom: 0,
          zIndex: 99,
          overflow: 'auto',
          height: 'calc(100vh - var(--header-height))',
        }}
      >
        {guest}
      </Sider>
    );
  }

  if (isMobile) {
    return (
      <Drawer
        title="Menu"
        placement="left"
        onClose={onMobileDrawerClose}
        open={mobileDrawerVisible}
        className="mobile-sidebar-drawer"
        width={280}
      >
        <div className="sidebar-nav-section">
          <div className="sidebar-nav-label">Main</div>
          <Menu
            mode="inline"
            selectedKeys={selectedKey ? [selectedKey] : []}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            className="sidebar-menu"
          >
            {renderMainMenuItems(MAIN_NAV_BEFORE_CHANNELS, false)}
            {renderChannelsSubMenu(false)}
            {renderMainMenuItems(MAIN_NAV_AFTER_CHANNELS, false)}
          </Menu>
        </div>
        {isAdmin && (
          <div className="sidebar-nav-section">
            <div className="sidebar-nav-label">Admin</div>
            <Menu mode="inline" selectedKeys={selectedKey ? [selectedKey] : []} className="sidebar-menu">
              {renderAdminItems(false)}
            </Menu>
          </div>
        )}
      </Drawer>
    );
  }

  return (
    <Sider
      width={240}
      collapsedWidth={72}
      className="app-sidebar"
      collapsed={collapsed}
      trigger={null}
      style={{
        position: 'fixed',
        left: 0,
        top: 'var(--header-height)',
        bottom: 0,
        zIndex: 99,
        overflow: 'auto',
        height: 'calc(100vh - var(--header-height))',
      }}
    >
      <div className="sidebar-nav-section">
        {!collapsed && <div className="sidebar-nav-label">Main</div>}
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={setOpenKeys}
          className="sidebar-menu"
          inlineCollapsed={collapsed}
        >
          {renderMainMenuItems(MAIN_NAV_BEFORE_CHANNELS, !collapsed)}
          {renderChannelsSubMenu(!collapsed)}
          {renderMainMenuItems(MAIN_NAV_AFTER_CHANNELS, !collapsed)}
        </Menu>
      </div>

      {isAdmin && (
        <div className="sidebar-nav-section">
          {!collapsed && <div className="sidebar-nav-label">Admin</div>}
          <Menu
            mode="inline"
            selectedKeys={selectedKey ? [selectedKey] : []}
            className="sidebar-menu"
            inlineCollapsed={collapsed}
          >
            {renderAdminItems(!collapsed)}
          </Menu>
        </div>
      )}
    </Sider>
  );
};

export default Sidebar;
